import { AnimatePresence } from "framer-motion";
import { useContext, useEffect, useState } from "react";
import { useBoolean, useMount, useTitle, useToggle } from "react-use";
import styled from "styled-components";
import { Box } from "../components/Box";
import { Container } from "../components/Container";
import { LayoutScroll } from "../layout/LayoutScroll";
import { SuperText } from "../components/SuperText";
import { TimelineIntro } from "../components/timeline/TimelineIntro";
import { TimelinePagination } from "../components/timeline/TimelinePagination";

import { ScrollTransform } from "../components/ScrollTransform";
import { LogoStyleContext } from "../context/LogoStyleContext";

import { InViewTrigger } from "../components/timeline/InViewTrigger";
import {
  bodyColorMap,
  TimelineTheme,
} from "../components/timeline/TimelineBackground";
import { Color } from "../models/theme";
import { timelineConfig, TimelineGroupData } from "../mock/timeline";
import { tail } from "lodash";
import { TimelineGroup } from "../components/timeline/TimelineGroup";
import { TimelineItem } from "../components/timeline/TimelineItem";
import { TimelineScrollAnimator } from "../components/timeline/TimelineScrollAnimator";
import { TimelineScrollFade } from "../components/timeline/TimelineScrollFade";
import { TimelineScrollTop } from "../components/timeline/TimelineScrollTop";
import { useSetLogoStyle } from "../hooks/useSetLogoStyle";

const DateContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const timelineConfigTail = tail(timelineConfig.groups);

export const Timeline: React.FC = () => {
  const [showIntro, setShowIntro] = useBoolean(true);
  const [isIntroDone, setIsIntroDone] = useBoolean(false);

  const [bgColor, setBgColor] = useState<Color>("salmon");

  const [locked, toggleLocked] = useToggle(true);

  const [activeIndex, setActiveIndex] = useState(0);

  // useLockBodyScroll(locked);

  const handleComplete = () => {
    // toggleLocked();
    setIsIntroDone(true);
  };

  const handleExitComplete = () => {
    setShowIntro(false);
  };

  const handleNextClick = () => {
    setShowIntro(false);
    setIsIntroDone(true);

    toggleLocked();
  };

  const handleInView = (timelineGroup: TimelineGroupData) => {
    setBgColor(timelineGroup.bgColor);
    setActiveIndex(timelineGroup.id);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0 });
  };

  useMount(() => {
    setTimeout(() => {
      scrollToTop();
    }, 1000);
  });

  useSetLogoStyle("dark");

  useTitle("Pharmacy in time | Boots Exhibition");

  return (
    <LayoutScroll>
      <TimelineScrollAnimator />
      <AnimatePresence onExitComplete={handleExitComplete}>
        {showIntro && (
          <TimelineIntro
            onComplete={handleComplete}
            onScrollTrigger={handleNextClick}
          ></TimelineIntro>
        )}
      </AnimatePresence>

      <TimelinePagination
        hidden={!isIntroDone}
        items={timelineConfig.groups}
        activeIndex={activeIndex}
        theme={bodyColorMap[bgColor]}
      />
      <TimelineTheme bgColor={bgColor}>
        <TimelineScrollFade>
          <InViewTrigger
            onInView={() => handleInView(timelineConfig.groups[0])}
          >
            <Box pt={6} id={timelineConfig.groups[0].id.toString()}>
              <Container>
                <DateContainer>
                  <Box zIndex={3} position="relative">
                    <ScrollTransform
                      moveFactor={["0vw", "-20vw"]}
                      startOffset={0}
                    >
                      <SuperText color="blueDark">1841</SuperText>
                    </ScrollTransform>
                  </Box>
                </DateContainer>
              </Container>
            </Box>
            <Box>
              <Container>
                {timelineConfig.groups[0].timelineItems.map((timelineItem) => (
                  <TimelineItem
                    groupActive={true}
                    key={timelineItem.id}
                    timelineItem={timelineItem}
                  />
                ))}
              </Container>
            </Box>
          </InViewTrigger>
          {timelineConfigTail.map((timelineGroup, i) => (
            <InViewTrigger
              key={timelineGroup.id}
              onInView={() => handleInView(timelineGroup)}
            >
              <TimelineGroup
                active={i === activeIndex - 1}
                timelineGroup={timelineGroup}
              />
            </InViewTrigger>
          ))}
        </TimelineScrollFade>
      </TimelineTheme>
      <TimelineScrollTop />
    </LayoutScroll>
  );
};
